<script setup>
import moment from "moment";
import {Vector as VectorLayer, Tile as TileLayer} from 'ol/layer';
import {Circle, Fill, Text, Stroke, Style, Icon} from 'ol/style';
import Feature from 'ol/Feature';
import {LineString, Point, Polygon} from 'ol/geom';
import Select from 'ol/interaction/Select';
import {fromLonLat, toLonLat, transformExtent} from 'ol/proj';
import Map from 'ol/Map';
import View from 'ol/View';
import XYZ from 'ol/source/XYZ';
import {onMounted, ref, watch} from "vue";
import Overlay from "ol/Overlay";
import axios from "axios";
import {Vector as VectorSource} from 'ol/source';
import useTreeLayers from "@/components/maps/inspection/composables/useTreeLayers.js";
import defaultTileLayers from "@/config/defaultTileLayers.js";
import AddressSearch from "@/components/maps/shared/AddressSearch.vue";
import useMapImagery from "@/components/maps/shared/composables/useMapImagery.js";
const props = defineProps({
    year: {
        type: Number,
        default: moment().format("YYYY")
    },
    selectedSpecies: {
        type: Array,
        default: () => []
    },
    species: {
        type: Array,
        default: () => []
    },
    getPlantings: {
        type: Function,
        default: () => {}
    }
})

const inspections = ref(null)
const tasks = ref(null)
const trees = ref(null)
const today = ref(moment().format("dddd Do MMM YYYY"))
const currYear = ref(parseInt(moment().format("YYYY")))
const overlay = ref(null)
const container = ref(null)
const content = ref(null)
const closer = ref(null)
const selFeature = ref(null)
const speciesChange = ref(null)
const yearChange = ref(null)
const updatePlanting = ref(false)
const saving = ref(false)
const zoomMinMax = ref(18)
const map = ref(null)
const satellite = ref(defaultTileLayers().satelliteTileLayer);
const osm = ref(defaultTileLayers().osmTileLayer);
const labels = ref(defaultTileLayers().labelsTileLayer);
const aerial = ref(defaultTileLayers().aerialTileLayer);
const imageService = ref(new TileLayer({ visible: false }));
const aerialGroup = ref(null);



const {treeLayer, allTrees, treeSource} = useTreeLayers({
    map,
    zoomMinMax,
});

// const {baseMapSwitch, addAdditionalLayers} = useMapImagery({
//     map,
//     osmLayer: osm,
//     imageService,
//     aerialGroup,
//     labelLayer: labels,
//     satelliteLayer:satellite,
//     aerialLayer:aerial,
// })

const cache = {}
const getCircleStyle = (sId) => {
    var key = 'circle' + sId;
    if (!cache[key]) {
        cache[key] = new Style({
            image: new Circle({
                radius: 8,
                fill: new Fill({
                    color: getRandomRgb()
                }),
                stroke: new Stroke({
                    color: 'white',
                    width: 3
                })
            })
        });
    }
    return cache[key];
}

const getStyle = (feature) => {
    const sId = feature.get('speciesId');
    if (!props.selectedSpecies.includes(sId)) {
        return new Style();
    }
    return getCircleStyle(sId);
}

const getTrees = async() => {
    return axios.get('/api/tree/planting/quick-map/' + props.year).then(response => response.data.trees)
}

const loadTreesToSource = (trees) => {
    treeSource.value.clear()
    const features = [];
    Object.keys(trees).forEach(key => {
        features[key] = new Feature(
            new Point(fromLonLat([
                parseFloat(trees[key].lng),
                parseFloat(trees[key].lat)
            ], 'EPSG:3857'))
        );
        features[key].set('treeId', trees[key].id);
        features[key].set('speciesId', trees[key].species_id);
        features[key].set('planting_year', trees[key].planting_year);
        features[key].set('common_name', trees[key].common_name);
        features[key].set('genus', trees[key].genus);
        features[key].set('species', trees[key].species);
        features[key].set('variety', trees[key].variety);
        features[key].set('notes', trees[key].notes);
    });
    treeSource.value.addFeatures(features);
}

const addTreeLayerToMap = () => {
    treeLayer.value = new VectorLayer({
        source: treeSource.value,
        style: getStyle
    });
    map.value.addLayer(treeLayer.value);
}

const selectStyle = new Style({
    image: new Circle({
        radius: 8,
        fill: new Fill({
            color: 'rgba(255, 255, 126, 0.8)',
        }),
        stroke: new Stroke({
            color: 'rgba(255, 255, 126, 0.5)',
            width: 4
        })
    })
});

const selectTree = new Select({
    layer: treeLayer.value,
    style: selectStyle
});

const addSelectToMap = () => {
    map.value.addInteraction(selectTree)
    selectTree.on('select', (e) => {
        var s = e.target.getFeatures().getArray();
        if (s.length === 1) {
            selFeature.value = s[0];
            speciesChange.value = selFeature.value.get('speciesId');
            yearChange.value = selFeature.value.get('planting_year');
            overlay.value.setPosition(selFeature.value.getGeometry().getCoordinates());
        }
    });
}

const updateFeature = () => {
    treeSource.value.changed();
}

const updateLocation = () => {
    saving.value = true;
    axios.post('api/tree/planting/quick-update', {
        'treeId': selFeature.value.get('treeId'),
        'sid': speciesChange.value,
        'plantingYear': yearChange.value
    }).then((response) => {
        saving.value = false;
        selFeature.value.set('speciesId', speciesChange.value);
        selFeature.value.set('planting_year', yearChange.value);
        overlay.value.setPosition(undefined);
        closer.value.blur();
        props.getPlantings();
        updateFeature();
    })
    .catch((error) => {
        console.log(error);
    });
}

const getRandomRgb = () => {
    const num = Math.round(0xffffff * Math.random());
    const r = num >> 16;
    const g = num >> 8 & 255;
    const b = num & 255;
    return 'rgb(' + r + ', ' + g + ', ' + b + ')';
}

onMounted(async () => {
    const response = await axios.get('/api/defaults');
    const defaultsList = response.data;
    const homeLng = parseFloat(defaultsList.homeCenter.lng);
    const homeLat = parseFloat(defaultsList.homeCenter.lat);
    const homeZoom = parseFloat(defaultsList.homeCenter.zoom);

    container.value = document.getElementById('popup');
    content.value = document.getElementById('popup-content');
    closer.value = document.getElementById('popup-closer');

    overlay.value = new Overlay({
        element: container.value,
        autoPan: true,
        autoPanAnimation: {
            duration: 250
        }
    });

    closer.value.onclick = () => {
        overlay.value.setPosition(undefined);
        closer.value.blur();
    };
    try {


        map.value = new Map({
            target: 'map',
            overlays: [overlay.value],
            layers: [
                osm.value,
                satellite.value
            ],
            view: new View({
                projection: 'EPSG:3857',
                center: fromLonLat([homeLng, homeLat], 'EPSG:3857'),
                zoom: homeZoom
            })
        });
    } catch(error){
        console.log('Failed to create map', error.message);
    }

    treeSource.value = new VectorSource();
    const trees = await getTrees()
    loadTreesToSource(trees)
    addTreeLayerToMap()
    addSelectToMap()
    // addAdditionalLayers()
});

watch(() => props.selectedSpecies, () => {
    updateFeature()
}, {deep: true})

watch(() => props.year, async (newVal, oldVal) => {
    if (newVal !== oldVal) {
        const trees = await getTrees()
        loadTreesToSource(trees)
    }
}, {deep: true})
</script>

<template>
    <div class="w-full relative">
        <div id="map" style="height:60vh">
<!--            <address-search :toggle-map="baseMapSwitch" :map="map" class="mt-4 mb-4"/>-->
        </div>
            <div id="popup" class="ol-popup">
            <a href="#" id="popup-closer" class="ol-popup-closer"></a>
            <div id="popup-content" class="text-slate-600">
                <div v-if="selFeature">
                    <div class="text-slate-700 font-semibold text-2xl">{{ selFeature.get('planting_year') }}</div>
                    <p class="text-slate-600 font-semibold">{{ selFeature.get('genus') }}</p>
                    <p class="text-slate-600">{{ selFeature.get('common_name') }}</p>
                    <p class="text-slate-600">{{ selFeature.get('species') }}</p>
                    <p class="text-slate-600">{{ selFeature.get('variety') }}</p>
                    <p class="text-slate-600 text-sm">{{ selFeature.get('notes') }}</p>

                    <div v-if="updatePlanting" class="border-slate-200 border-t mt-2">
                        <div class="text-sm">
                            <p class="text-sm text-slate-700 font-semibold pb-2">
                                Year
                            </p>
                            <select
                                v-model="yearChange"
                                class="block appearance-none w-full bg-slate-100 border border-slate-200 text-slate-700 py-3 px-4 pr-8 rounded-sm leading-tight focus:outline-none focus:bg-white focus:border-slate-500"
                            >
                                <option :value="currYear">
                                    {{ currYear }}
                                </option>
                                <option v-for="n in 5" :key="n" :value="currYear + n">
                                    {{ currYear + n }}
                                </option>
                            </select>
                        </div>
                        <div class="text-sm">
                            <p class="text-sm text-slate-700 font-semibold pb-2">
                                Species
                            </p>
                            <select
                                v-model="speciesChange"
                                class="block appearance-none w-full bg-slate-100 border border-slate-200 text-slate-700 py-3 px-4 pr-8 rounded-sm leading-tight focus:outline-none focus:bg-white focus:border-slate-500"
                            >
                                <option v-for="item in species" :value="item.id" :key="item.id">
                                    {{ item.gname }} {{ item.species }} {{ item.variety }} - {{ item.cname }}
                                </option>
                            </select>
                        </div>
                    </div>

                    <div v-if="!saving" class="flex justify-between border-slate-200 border-t mt-2">
                        <button
                            @click.prevent="updatePlanting = !updatePlanting"
                            class="w-1/3 p-2 mb-2 mt-4 bg-slate-200 hover:bg-slate-400 text-slate-600 py-2 rounded-lg">
                            {{ updatePlanting ? 'Cancel' : 'Update' }}
                        </button>
                        <button
                            v-if="updatePlanting"
                            @click.prevent="updateLocation()"
                            class="w-1/3 p-2 mb-2 mt-4 bg-green-300 hover:bg-green-400 text-green-900 py-2 rounded-lg">
                            Update
                        </button>
                    </div>
                    <div v-else class="flex justify-between border-slate-200 border-t mt-2 text-green-700">
                        Saving!
                    </div>
                </div>
            </div>
        </div>
        <p class="text-blue-600 pt-4 font-semibold">Select one or more Species below to see their locations on the
            map</p>
        <p class="text-slate-500 pb-4">Click the dots on the map to update the Year or Species</p>
    </div>
</template>

<style scoped>
.map-height {
    height: 50vh;
}

.ol-popup {
    position: absolute;
    background-color: white;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
    padding: 15px;
    border-radius: 10px;
    border: 1px solid #cccccc;
    bottom: 12px;
    left: -50px;
    min-width: 280px;
}

.ol-popup:after, .ol-popup:before {
    top: 100%;
    border: solid transparent;
    content: " ";
    height: 0;
    width: 0;
    position: absolute;
    pointer-events: none;
}

.ol-popup:after {
    border-top-color: white;
    border-width: 10px;
    left: 48px;
    margin-left: -10px;
}

.ol-popup:before {
    border-top-color: #cccccc;
    border-width: 11px;
    left: 48px;
    margin-left: -11px;
}

.ol-popup-closer {
    text-decoration: none;
    position: absolute;
    top: 2px;
    right: 8px;
}

.ol-popup-closer:after {
    content: "\274c";
}

.ol-zoom .ol-zoom-in {
    border-radius: 4px 4px 0 0 !important;
}

.ol-zoom .ol-zoom-out {
    border-radius: 0 0 4px 4px !important;
}

.ol-control button {
    height: 2em !important;
    width: 2em !important;
}
</style>